<template>
  <div
    v-if="visibleForms.length > 0"
    class="settings-section"
  >
    <TriggerCardSettingRow
      v-for="form in visibleForms"
      :key="form.actionDetailKey"
      :label="i18n.t(`trigger.actionSetting.cardLabels.${form.inputType}`)"
    >
      <template v-if="form.inputType === 'sequence_select'">
        <SequenceMasterSimpleSelect
          :model-value="numberValueOf(form)"
          is-display-only
          hide-link
          wait-key-suffix="CardSettings"
        />
      </template>
      <template v-else-if="form.inputType === 'sequence_owner_select'">
        <SelectSequenceOwner
          :model-value="sequenceOwnerValueOf(form)"
          is-display-only
        />
      </template>
      <template v-else-if="form.inputType === 'lead_stage_select'">
        <LeadStageSimpleSelect
          :model-value="numberValueOf(form)"
          is-display-only
          wait-key-suffix="CardSettings"
        />
      </template>
    </TriggerCardSettingRow>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { TriggerActionBody, TriggerActionForm } from '@/api/openapi';
import LeadStageSimpleSelect from './LeadStageSimpleSelect.vue';
import SequenceMasterSimpleSelect from './SequenceMasterSimpleSelect.vue';
import SelectSequenceOwner, { TSequenceOwnerValue } from './triggerActionForm/SelectSequenceOwner.vue';
import TriggerCardSettingRow from './TriggerCardSettingRow.vue';

type TProps = {
  triggerAction: TriggerActionBody | null;
  forms: TriggerActionForm[];
};
const props = defineProps<TProps>();

const i18n = useI18n();

// actionDetail は OpenAPI 上 object 型（キー不定のバッグ）。index アクセスのため
// Record<string, unknown> として扱い、値は表示時に各 model 型へ絞る。
// キャストは vue-eslint-parser が template 内の union 型をフィルタ構文と誤認するため、
// template ではなく script 側のゲッターで行う。
const actionDetail = computed(() => {
  const detail = props.triggerAction?.actionDetail ?? {};
  return detail as Record<string, unknown>;
});

const numberValueOf = (form: TriggerActionForm) =>
  actionDetail.value[form.actionDetailKey] as number | null;

const sequenceOwnerValueOf = (form: TriggerActionForm) =>
  actionDetail.value[form.actionDetailKey] as TSequenceOwnerValue;

// 設定済み（値が入っている）項目だけを、forms の順序で表示する。
const hasValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'string') return value !== '';
  return true;
};

const visibleForms = computed(() =>
  props.forms.filter((form) => hasValue(actionDetail.value[form.actionDetailKey])),
);
</script>

<style lang="scss" scoped>
.settings-section {
  padding: 12px 20px;
  border-top: 1px solid $bdcolor-light;
}
</style>
